/**
 * Centralized keyboard shortcut registry.
 *
 * Every keyboard shortcut in the app is defined here. Both the dispatch
 * logic (useKeyboardShortcuts) and the UI display (tooltips, Kbd chips)
 * derive from this single source of truth.
 *
 * Contexts determine when a binding is active:
 *   command — Active during command buffer input (after G/R/S)
 *   edit    — Active only in edit mode
 *   object  — Active when NOT in edit or pose mode
 *   pose    — Active in pose mode
 *   global  — Active in all modes (lowest priority)
 *
 * Lookup priority: command > edit > object > pose > global.
 * Within a context, the first matching binding wins (array order).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type KeyContext =
  | "command"
  | "edit"
  | "sculpt"
  | "sketch"
  | "object"
  | "pose"
  | "global";

export interface KeyBinding {
  /** Lowercase key identifier (e.g., "z", "f3", "escape", ",") */
  key: string;
  /** Action identifier dispatched to the handler */
  action: string;
  /** Context in which this binding applies */
  context: KeyContext;
  /** Requires Ctrl (Windows/Linux) or Cmd (Mac) */
  mod?: boolean;
  /** Requires Shift */
  shift?: boolean;
  /** Requires Alt (Option on Mac) */
  alt?: boolean;
  /** Override the auto-generated display label (e.g., "GG" for edge slide) */
  display?: string;
}

// ---------------------------------------------------------------------------
// Camera / zoom hold-keys (tracked as a Set, not dispatched as actions)
// ---------------------------------------------------------------------------

/** Maps e.code → camera key identifier. Active during modal fly mode (Shift+`). */
export const CAMERA_KEYS: ReadonlyMap<string, string> = new Map([
  ["KeyW", "w"],
  ["KeyA", "a"],
  ["KeyS", "s"],
  ["KeyD", "d"],
  ["KeyE", "e"],
  ["KeyQ", "q"],
]);

/** Keys (by e.key or e.code) that trigger zoom in while held. */
export const ZOOM_IN_KEYS = new Set(["=", "+", "Equal", "NumpadAdd"]);

/** Keys (by e.key or e.code) that trigger zoom out while held. */
export const ZOOM_OUT_KEYS = new Set(["-", "_", "Minus", "NumpadSubtract"]);

// ---------------------------------------------------------------------------
// Keymap
// ---------------------------------------------------------------------------

export const KEYMAP: readonly KeyBinding[] = [
  // ── Command buffer (active after G/R/S) ──────────────────────────────
  { key: "escape", action: "command.cancel", context: "command" },
  { key: "enter", action: "command.confirm", context: "command" },
  { key: "x", action: "command.axis.x", context: "command" },
  { key: "y", action: "command.axis.y", context: "command" },
  { key: "z", action: "command.axis.z", context: "command" },
  { key: "backspace", action: "command.backspace", context: "command" },
  // G in translate → edge slide is handled as a special case in the handler.
  // Display-only entry so getShortcutLabel("edge_slide") returns "GG":
  { key: "g", action: "edge_slide", context: "command", display: "GG" },

  // ── Global (always active outside command buffer / form fields) ──────
  { key: "f9", action: "toggleRedoPanel", context: "global" },
  { key: "f3", action: "toggleOperatorSearch", context: "global" },
  { key: "z", action: "redo", context: "global", mod: true, shift: true },
  { key: "z", action: "undo", context: "global", mod: true },
  { key: "y", action: "redo", context: "global", mod: true },
  {
    key: "s",
    action: "saveSceneAs",
    context: "global",
    mod: true,
    shift: true,
  },
  { key: "s", action: "saveScene", context: "global", mod: true },
  { key: "n", action: "newScene", context: "global", mod: true },
  { key: "o", action: "openScene", context: "global", mod: true },
  { key: "l", action: "backToLibrary", context: "global", mod: true },
  {
    key: ",",
    action: "openRenderMethodModal",
    context: "global",
    mod: true,
    shift: true,
  },
  { key: ",", action: "toggleSettings", context: "global", mod: true },
  { key: ".", action: "toggleDebug", context: "global", mod: true },
  { key: "a", action: "toggleAddObject", context: "global", shift: true },
  { key: "a", action: "selectAll", context: "global" },
  { key: "tab", action: "toggleEditPoseMode", context: "global" },
  { key: "z", action: "cycleRenderMethod", context: "global" },
  // Backtick opens the axis-view pie (Blender grave-accent). Shift+~ remains
  // the fly-mode toggle below. Match lives in useKeyboardShortcuts too via
  // e.code === "Backquote" to cover keyboard layouts where ` isn't the
  // unshifted Backquote character.
  { key: "`", action: "openViewPie", context: "global" },
  { key: "g", action: "translate", context: "global" },
  { key: "r", action: "rotate", context: "global" },
  { key: "s", action: "scale", context: "global" },
  { key: "i", action: "refImageMode", context: "global" },
  { key: "escape", action: "deselect", context: "global" },
  { key: "o", action: "toggleProportional", context: "edit" },
  { key: "tab", action: "toggleSnap", context: "global", shift: true },
  { key: "/", action: "openDebugConsole", context: "global", mod: true },
  { key: "d", action: "duplicate", context: "global", shift: true },
  {
    key: "~",
    action: "toggleFlyMode",
    context: "global",
    shift: true,
    display: "Shift+`",
  },

  // ── Axis-aligned view switcher (Alt+1..6) ────────────────────────────
  // Each shortcut toggles its view — press while already in that view to
  // exit back to the pre-lock perspective / focal length.
  { key: "1", action: "view.axis.front", context: "global", alt: true },
  { key: "2", action: "view.axis.back", context: "global", alt: true },
  { key: "3", action: "view.axis.left", context: "global", alt: true },
  { key: "4", action: "view.axis.right", context: "global", alt: true },
  { key: "5", action: "view.axis.top", context: "global", alt: true },
  { key: "6", action: "view.axis.bottom", context: "global", alt: true },

  { key: "/", action: "toggleDock", context: "global" },
  // 3D cursor (Blender-style Shift+C / Shift+S pie)
  { key: "c", action: "cursor.toWorldOrigin", context: "global", shift: true },
  { key: "s", action: "openSnapPie", context: "global", shift: true },

  // ── Pose mode ────────────────────────────────────────────────────────
  { key: "x", action: "relax_bones", context: "pose" },
  { key: "f", action: "drop", context: "pose" },
  { key: "p", action: "pin_joint", context: "pose" },

  // ── Edit mode ────────────────────────────────────────────────────────
  { key: "a", action: "deselectAll", context: "edit", alt: true },
  { key: "v", action: "openVertexMenu", context: "edit", mod: true },
  { key: "e", action: "openEdgeMenu", context: "edit", mod: true },
  { key: "f", action: "openFaceMenu", context: "edit", mod: true },
  { key: "x", action: "openDeleteMenu", context: "edit" },
  { key: "delete", action: "openDeleteMenu", context: "edit" },
  { key: "m", action: "openMergeMenu", context: "edit" },
  { key: "e", action: "extrude", context: "edit" },
  { key: "e", action: "openExtrudePie", context: "edit", alt: true },
  { key: "i", action: "inset_faces", context: "edit" },
  { key: "b", action: "box_select", context: "edit" },
  { key: "c", action: "brush_select", context: "edit" },
  { key: "b", action: "bevel", context: "edit", mod: true },
  { key: "r", action: "insert_edge_loop", context: "edit", mod: true },
  { key: "f", action: "fill", context: "edit" },
  { key: "j", action: "connect_vertex_path", context: "edit" },
  { key: "l", action: "select_connected_add", context: "edit", shift: true },
  { key: "l", action: "select_connected", context: "edit" },
  { key: "1", action: "granularity.vertex", context: "edit" },
  { key: "2", action: "granularity.edge", context: "edit" },
  { key: "3", action: "granularity.face", context: "edit" },
  { key: "p", action: "separate", context: "edit" },
  { key: "s", action: "shrink_fatten", context: "edit", alt: true },
  { key: "o", action: "smooth", context: "edit", shift: true },
  { key: "a", action: "skin_radius", context: "edit", mod: true },
  { key: "=", action: "selectMore", context: "edit", mod: true },
  { key: "-", action: "selectLess", context: "edit", mod: true },

  { key: "h", action: "hideSelection", context: "edit" },
  { key: "h", action: "hideUnselected", context: "edit", shift: true },
  { key: "h", action: "unhideAll", context: "edit", alt: true },
  { key: "z", action: "toggleEditXray", context: "edit", alt: true },
  { key: "v", action: "rip", context: "edit" },
  // ── Curve edit mode ──────────────────────────────────────────────────
  // Note: V is handled context-aware: mesh → rip, curve → handle type menu

  // ── Sculpt mode ─────────────────────────────────────────────────────
  { key: "1", action: "sculpt.brush.draw", context: "sculpt" },
  { key: "2", action: "sculpt.brush.smooth", context: "sculpt" },
  { key: "3", action: "sculpt.brush.flatten", context: "sculpt" },
  { key: "4", action: "sculpt.brush.grab", context: "sculpt" },
  { key: "f", action: "sculpt.invert.toggle", context: "sculpt" },
  { key: "[", action: "sculpt.radius.down", context: "sculpt" },
  { key: "]", action: "sculpt.radius.up", context: "sculpt" },

  // ── Sketch mode ────────────────────────────────────────────────────
  { key: "1", action: "sketch.tool.pencil", context: "sketch" },
  { key: "2", action: "sketch.tool.erase", context: "sketch" },
  { key: "[", action: "sketch.radius.down", context: "sketch" },
  { key: "]", action: "sketch.radius.up", context: "sketch" },

  // ── Object mode ──────────────────────────────────────────────────────
  { key: "b", action: "box_select", context: "object" },
  { key: "c", action: "brush_select", context: "object" },
  { key: "h", action: "hideSelection", context: "object" },
  { key: "h", action: "hideUnselected", context: "object", shift: true },
  { key: "h", action: "unhideAll", context: "object", alt: true },
  { key: "z", action: "toggleObjectXray", context: "object", alt: true },
  { key: "a", action: "deselectAll", context: "object", alt: true },
  { key: "j", action: "joinObjects", context: "object", mod: true },
  { key: "j", action: "mergeObjects", context: "object", mod: true, alt: true },
  { key: "x", action: "openObjectDeleteConfirm", context: "object" },

  // ── Shared (object + edit) ───────────────────────────────────────────
  { key: "i", action: "invertSelection", context: "global", mod: true },
  { key: ".", action: "focusSelection", context: "global" },
];

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/** Context priority — more specific contexts checked first. */
const CONTEXT_PRIORITY: readonly KeyContext[] = [
  "command",
  "edit",
  "sculpt",
  "sketch",
  "object",
  "pose",
  "global",
];

/**
 * Returns true when the keyboard event targets a real editable field
 * (text input, textarea, select, contentEditable) where shortcuts should
 * yield to normal text entry.  Read-only and disabled inputs (e.g. the
 * iPad Safari focus-sink) are intentionally excluded so that keyboard
 * shortcuts remain active even when they hold focus.
 */
export function isEditableField(e: KeyboardEvent): boolean {
  const el = e.target as HTMLElement;
  if (!el?.tagName) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName === "INPUT") {
    const input = el as HTMLInputElement;
    return !input.readOnly && !input.disabled;
  }
  return false;
}

/** Normalize a KeyboardEvent key to our canonical lowercase format.
 *  When Alt/Option is held on macOS, `e.key` returns a dead-key character
 *  (e.g. Option+A → "å", Option+1 → "¡") instead of the base key. Fall
 *  back to `e.code` for both letters and digits so the keymap matches
 *  consistently across platforms.
 */
export function normalizeKey(e: KeyboardEvent): string {
  if (e.altKey) {
    if (e.code.startsWith("Key")) return e.code.slice(3).toLowerCase(); // "KeyA" → "a"
    if (e.code.startsWith("Digit")) return e.code.slice(5); // "Digit1" → "1"
  }
  return e.key.toLowerCase();
}

/**
 * Find the action for a key event given the currently active contexts.
 * Returns the action string, or null if no binding matches.
 */
export function lookupAction(
  key: string,
  mod: boolean,
  shift: boolean,
  activeContexts: readonly KeyContext[],
  alt?: boolean,
): string | null {
  const altPressed = alt ?? false;
  for (const ctx of CONTEXT_PRIORITY) {
    if (!activeContexts.includes(ctx)) continue;
    for (const b of KEYMAP) {
      if (b.context !== ctx) continue;
      if (b.key !== key) continue;
      if ((b.mod ?? false) !== mod) continue;
      if ((b.shift ?? false) !== shift) continue;
      if ((b.alt ?? false) !== altPressed) continue;
      return b.action;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.userAgent);

/**
 * Get the human-readable shortcut label for an action.
 * Returns undefined if the action has no binding in the keymap.
 */
export function getShortcutLabel(action: string): string | undefined {
  const binding = KEYMAP.find((b) => b.action === action);
  if (!binding) return undefined;
  if (binding.display) return binding.display;

  const keyLabel =
    binding.key.length === 1
      ? binding.key.toUpperCase()
      : binding.key.charAt(0).toUpperCase() + binding.key.slice(1);

  if (IS_MAC) {
    const mods =
      (binding.alt ? "⌥" : "") +
      (binding.shift ? "⇧" : "") +
      (binding.mod ? "⌘" : "");
    return mods + keyLabel;
  }

  const parts: string[] = [];
  if (binding.mod) parts.push("Ctrl");
  if (binding.alt) parts.push("Alt");
  if (binding.shift) parts.push("Shift");
  parts.push(keyLabel);
  return parts.join("+");
}
